import { useState } from 'react'
import type { MetaFunction, LoaderFunctionArgs } from 'react-router'
import { useLoaderData, Link } from 'react-router'
import { db } from '@afficheservices/db'
import { professionalProfiles } from '@afficheservices/db/schema'
import { eq } from 'drizzle-orm'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { getSessionUser } from '~/lib/auth.server'
import {
  Gift, Euro, Copy, CheckCircle, Share2,
  ArrowRight, Star, Link as LinkIcon,
} from 'lucide-react'
import { STATIC_PAGE_HEADERS } from '~/lib/cache.server'

export function headers() { return STATIC_PAGE_HEADERS }

export const meta: MetaFunction = () => [
  { title: 'Programme de Parrainage — AfficheServices' },
  { name: 'description', content: 'Parrainez un artisan et gagnez des avantages. Programme de parrainage AfficheServices pour les professionnels du bâtiment.' },
]

const REWARDS = [
  { icon: Gift, title: '1 mois gratuit', desc: 'Le parrain reçoit 1 mois d\'abonnement offert', color: 'text-primary bg-primary/10' },
  { icon: Euro, title: '50% de réduction', desc: 'Le filleul bénéficie de 50% sur son 1er mois', color: 'text-emerald-600 bg-emerald-50' },
  { icon: Star, title: 'Badge Parrain', desc: 'Badge spécial visible sur votre profil', color: 'text-amber-600 bg-amber-50' },
]

const STEPS = [
  { step: '1', title: 'Partagez votre lien', desc: 'Copiez votre lien unique et envoyez-le à un collègue artisan' },
  { step: '2', title: 'Il s\'inscrit', desc: 'Votre filleul crée son compte professionnel avec votre code' },
  { step: '3', title: 'Vous gagnez tous les deux', desc: '1 mois gratuit pour vous, 50% de réduction pour lui' },
]

export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url)
  const code = url.searchParams.get('code') ?? ''

  // Check if referral code is valid
  let referrerName = ''
  if (code) {
    const [pro] = await db.select({ company: professionalProfiles.companyName })
      .from(professionalProfiles)
      .where(eq(professionalProfiles.slug, code))
      .limit(1)
    referrerName = pro?.company ?? ''
  }

  // If authenticated pro, get their personal referral code (slug)
  let mySlug = ''
  let myReferralCount = 0
  const user = await getSessionUser(request)
  if (user && user.role === 'professional') {
    const [pro] = await db
      .select({ slug: professionalProfiles.slug, metaData: professionalProfiles.metaData })
      .from(professionalProfiles)
      .where(eq(professionalProfiles.userId, user.id))
      .limit(1)
    if (pro?.slug) {
      mySlug = pro.slug
      const meta = (pro.metaData as Record<string, any>) ?? {}
      myReferralCount = Array.isArray(meta.referrals) ? meta.referrals.length : 0
    }
  }

  return { code, referrerName, mySlug, myReferralCount }
}

export default function ParrainagePage() {
  const { code, referrerName, mySlug, myReferralCount } = useLoaderData<typeof loader>()
  const [copied, setCopied] = useState(false)
  const baseUrl = 'https://www.afficheservices.fr'
  const referralLink = mySlug
    ? `${baseUrl}/register-pro?ref=${mySlug}`
    : `${baseUrl}/register-pro?ref=VOTRE-CODE`

  function copyLink() {
    navigator.clipboard.writeText(referralLink).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <>
      <Navbar />
      <main className="bg-gradient-to-b from-slate-50 to-white">
        {/* Hero */}
        <section className="px-4 py-16 text-center sm:py-20">
          <div className="mx-auto max-w-3xl">
            {referrerName && (
              <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary">
                <Gift className="h-4 w-4" /> Invité par {referrerName} — 50% de réduction sur votre 1er mois
              </div>
            )}
            <h1 className="text-4xl font-bold text-slate-900 sm:text-5xl">
              Parrainez un artisan,{' '}
              <span className="text-primary">gagnez ensemble</span>
            </h1>
            <p className="mt-4 text-lg text-slate-500">
              Recommandez AfficheServices à un collègue artisan.
              Vous recevez 1 mois gratuit, il bénéficie de 50% sur son premier mois.
            </p>
            {referrerName ? (
              <Link
                to={`/register-pro?ref=${code}`}
                className="mt-8 inline-flex items-center gap-2 rounded-xl bg-primary px-8 py-4 text-lg font-semibold text-white shadow-lg shadow-primary/25 hover:bg-primary/90 transition-all"
              >
                S'inscrire avec la réduction <ArrowRight className="h-5 w-5" />
              </Link>
            ) : (
              <Link
                to="/register-pro"
                className="mt-8 inline-flex items-center gap-2 rounded-xl bg-primary px-8 py-4 text-lg font-semibold text-white shadow-lg shadow-primary/25 hover:bg-primary/90 transition-all"
              >
                Devenir parrain <ArrowRight className="h-5 w-5" />
              </Link>
            )}
          </div>
        </section>

        {/* Rewards */}
        <section className="px-4 pb-16">
          <div className="mx-auto max-w-4xl">
            <h2 className="mb-8 text-center text-2xl font-bold text-slate-900">Vos avantages</h2>
            <div className="grid gap-6 sm:grid-cols-3">
              {REWARDS.map((r) => (
                <div key={r.title} className="rounded-2xl border border-slate-200 bg-white p-6 text-center">
                  <div className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl ${r.color}`}>
                    <r.icon className="h-7 w-7" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900">{r.title}</h3>
                  <p className="mt-2 text-sm text-slate-500">{r.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="bg-white px-4 py-16">
          <div className="mx-auto max-w-4xl">
            <h2 className="mb-10 text-center text-2xl font-bold text-slate-900">Comment ça marche</h2>
            <div className="grid gap-8 sm:grid-cols-3">
              {STEPS.map((s, _i) => (
                <div key={s.step} className="relative text-center">
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-xl font-bold text-white">
                    {s.step}
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-500">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Share link section */}
        <section className="px-4 py-16">
          <div className="mx-auto max-w-lg">
            <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
              <LinkIcon className="mx-auto mb-4 h-8 w-8 text-primary" />
              <h3 className="text-xl font-bold text-slate-900">Votre lien de parrainage</h3>
              {mySlug ? (
                <p className="mt-2 text-sm text-slate-500">
                  Partagez ce lien unique. <strong>{myReferralCount}</strong> filleul{myReferralCount !== 1 ? 's' : ''} inscrit{myReferralCount !== 1 ? 's' : ''}.
                </p>
              ) : (
                <p className="mt-2 text-sm text-slate-500">Connectez-vous à votre espace pro pour obtenir votre lien personnalisé</p>
              )}

              <div className="mt-6 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <input
                  type="text"
                  readOnly
                  value={referralLink}
                  className="flex-1 bg-transparent text-sm text-slate-600 outline-none"
                />
                <button
                  onClick={copyLink}
                  className={`flex items-center gap-1 rounded-lg px-4 py-2 text-sm font-medium transition-all ${copied ? 'bg-emerald-500 text-white' : 'bg-primary text-white hover:bg-primary/90'}`}
                >
                  {copied ? <><CheckCircle className="h-4 w-4" /> Copié</> : <><Copy className="h-4 w-4" /> Copier</>}
                </button>
              </div>

              {/* Share buttons */}
              <div className="mt-4 flex justify-center gap-3">
                <a
                  href={`https://wa.me/?text=Rejoins%20AfficheServices%20et%20reçois%20des%20demandes%20de%20devis%20qualifiées%20!%20${encodeURIComponent(referralLink)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-600"
                >
                  <Share2 className="h-4 w-4" /> WhatsApp
                </a>
                <a
                  href={`mailto:?subject=Rejoins AfficheServices&body=Salut ! Je te recommande AfficheServices pour recevoir des demandes de devis qualifiées. Inscris-toi ici : ${referralLink}`}
                  className="flex items-center gap-2 rounded-lg bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600"
                >
                  <Share2 className="h-4 w-4" /> Email
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-white px-4 py-16">
          <div className="mx-auto max-w-2xl">
            <h2 className="mb-8 text-center text-2xl font-bold text-slate-900">Questions fréquentes</h2>
            <div className="space-y-4">
              {[
                { q: 'Combien de personnes puis-je parrainer ?', a: 'Il n\'y a pas de limite ! Plus vous parrainez, plus vous cumulez de mois gratuits.' },
                { q: 'Quand est-ce que je reçois ma récompense ?', a: 'Votre mois gratuit est crédité dès que votre filleul active son abonnement.' },
                { q: 'Mon filleul doit-il souscrire un abonnement payant ?', a: 'Non, le programme s\'applique dès l\'inscription. La réduction de 50% s\'applique sur le premier mois d\'abonnement payant.' },
                { q: 'Comment mon filleul sait qu\'il a été parrainé ?', a: 'En utilisant votre lien ou code de parrainage lors de l\'inscription, la réduction est automatiquement appliquée.' },
              ].map((faq) => (
                <details key={faq.q} className="group rounded-xl border border-slate-200 bg-white">
                  <summary className="flex cursor-pointer items-center justify-between px-6 py-4 text-sm font-medium text-slate-900">
                    {faq.q}
                    <span className="ml-2 text-slate-400 transition-transform group-open:rotate-45">+</span>
                  </summary>
                  <p className="px-6 pb-4 text-sm text-slate-500">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
      {/* Referral FAQ */}
      <section className="border-t border-slate-100 bg-surface-secondary py-14">
        <div className="mx-auto max-w-3xl px-4 lg:px-6">
          <h2 className="text-center text-xl font-bold text-slate-900">Questions sur le parrainage</h2>
          <div className="mt-6 space-y-3">
            {[
              { q: 'Combien puis-je gagner en parrainant ?', a: 'Vous recevez un bonus pour chaque filleul qui finalise sa première demande de devis. Le montant varie selon les promotions en cours — consultez votre tableau de bord pour les conditions actuelles.' },
              { q: 'Y a-t-il une limite au nombre de parrainages ?', a: 'Non, vous pouvez parrainer autant de personnes que vous le souhaitez. Chaque parrainage validé vous rapporte un bonus, sans limite.' },
              { q: 'Comment mon filleul bénéficie-t-il du parrainage ?', a: 'Votre filleul reçoit également un avantage lors de sa première demande de devis. Il lui suffit d\'utiliser votre lien ou code de parrainage lors de son inscription.' },
            ].map((faq, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white p-5">
                <h3 className="text-sm font-semibold text-slate-900">{faq.q}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </>
  )
}
